/**
 * use-platform-ai.ts — Hook khu Platform cho "AI hai chế độ": khoá API của eCDP, danh mục model,
 * AI theo tổ chức (điều chỉnh credit, khoá), cài đặt credit. Gọi qua `platformApi` (token platform).
 * API chưa có (404) → query trả null để trang hiện trạng thái rỗng thay vì vỡ.
 * Hợp đồng: docs/AI-2-CHE-DO-HOP-DONG-API.md → "API Platform".
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import { isApiMissing, type CreditPack, type LedgerItem, type WalletStatus } from '@/lib/ai-credits'

export interface PlatformAiKey {
  id: string
  provider: string
  label: string | null
  hint: string | null
  status: 'active' | 'disabled' | string
  priority: number
  lastVerifiedAt: string | null
  lastError: string | null
  lastUsedAt: string | null
}

export type ModelKind = 'chat' | 'embedding'
export type MinPlan = 'free' | 'paid'

export interface CatalogModel {
  provider: string
  model: string
  title: string
  kind: ModelKind | string
  tier: string | null
  priceIn: number
  priceOut: number
  priceCacheRead: number | null
  priceCacheWrite: number | null
  dims: number | null
  managedEnabled: boolean
  minPlan: MinPlan
  active: boolean
  /** Cũ — giao diện thôi dùng. */
  creditsPerTurn: number | null
  /** Giá bán ra (đ / 1 triệu token) theo biên đang lưu. Server cũ: undefined. */
  salePricePer1M?: { inVnd: number; outVnd: number; blendedVnd: number }
  /** Model tham chiếu để quy "≈ token" ở Platform (mặc định nền tảng / rẻ nhất). */
  isReference?: boolean
}

export interface PlatformAiSettings {
  platformEnabled: boolean
  /** Đơn vị tính nội bộ của ví (đ / đơn vị) — chỉ đọc, không hiện cho khách. */
  creditValueVnd: number
  /** Biên giá bán ra (%): giá bán ra = giá mua vào × (1 + biên %). 0 = bán đúng giá mua vào. */
  markupPercent: number
  /** Giá trị 1 lượt AI (đ giá trị ví) — đơn vị khách thấy. Server cũ: undefined. */
  turnValueVnd?: number
  usdVnd: number
  embeddingOverageCreditsPer1000: number
  packs: CreditPack[]
}

export interface PlatformAiOrg {
  orgId: string
  name: string
  planId: string | null
  sourcesSummary: 'platform' | 'byok' | 'mixed' | string
  /** Đơn vị nội bộ (cũ) — giao diện dùng balanceVnd / balanceTokens. */
  balance: number
  usedThisMonth: number
  /** Giá trị đ theo giá bán ra. Server cũ: undefined. */
  balanceVnd?: number
  usedThisMonthVnd?: number
  /** ≈ token theo model tham chiếu (totals.referenceModel). */
  balanceTokens?: number | null
  status: WalletStatus | string
  blocked: boolean
}

export interface PlatformAiOrgsResponse {
  items: PlatformAiOrg[]
  /** grossMarginPercent null khi chưa dùng. */
  totals: {
    soldThisMonth: number; usedThisMonth: number; providerCostUsd: number; grossMarginPercent: number | null
    soldThisMonthVnd?: number; usedThisMonthVnd?: number
    referenceModel?: { provider: string; model: string; title: string } | null
  } | null
}

const K = {
  keys: ['platform', 'ai2', 'keys'] as unknown[],
  catalog: ['platform', 'ai2', 'catalog'] as unknown[],
  settings: ['platform', 'ai2', 'settings'] as unknown[],
  orgs: (q: string, status: string): unknown[] => ['platform', 'ai2', 'orgs', q, status],
  orgsAll: ['platform', 'ai2', 'orgs'] as unknown[],
  ledger: (orgId: string | null): unknown[] => ['platform', 'ai2', 'ledger', orgId],
}
export const platformAiKeys = K

async function getOptional<T>(url: string, params?: Record<string, unknown>): Promise<T | null> {
  try {
    return (await platformApi.get<T>(url, { params })).data
  } catch (err) {
    if (isApiMissing(err)) return null
    throw err
  }
}

/** Mảng, `{ items }` hoặc `{ <key> }` (vd `{ orgs }`) — chấp nhận các dạng trả về. */
function asList<T>(d: unknown, key = 'items'): T[] {
  if (Array.isArray(d)) return d as T[]
  const obj = d as Record<string, unknown> | null
  const list = obj?.[key] ?? obj?.items
  return Array.isArray(list) ? (list as T[]) : []
}

// ── Khoá API của eCDP ─────────────────────────────────────────────
export function usePlatformAiKeys() {
  return useQuery<PlatformAiKey[] | null>({
    queryKey: K.keys,
    queryFn: async () => {
      const d = await getOptional<unknown>('/platform/ai/keys')
      return d === null ? null : asList<PlatformAiKey>(d)
    },
    retry: false,
  })
}

export function useCreatePlatformAiKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { provider: string; apiKey: string; label?: string; priority?: number }) =>
      (await platformApi.post('/platform/ai/keys', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.keys }),
  })
}

export function useUpdatePlatformAiKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; label?: string; priority?: number; status?: 'active' | 'disabled'; apiKey?: string }) =>
      (await platformApi.patch(`/platform/ai/keys/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.keys }),
  })
}

export function useDeletePlatformAiKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await platformApi.delete(`/platform/ai/keys/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.keys }),
  })
}

export function useVerifyPlatformAiKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await platformApi.post<{ ok?: boolean; error?: string }>(`/platform/ai/keys/${id}/verify`)).data,
    onSettled: () => qc.invalidateQueries({ queryKey: K.keys }),
  })
}

// ── Danh mục model ────────────────────────────────────────────────
export function usePlatformAiCatalog() {
  return useQuery<CatalogModel[] | null>({
    queryKey: K.catalog,
    queryFn: async () => {
      const d = await getOptional<unknown>('/platform/ai/catalog')
      return d === null ? null : asList<CatalogModel>(d)
    },
    retry: false,
  })
}

export type CatalogPatch = Partial<Pick<CatalogModel,
  'title' | 'tier' | 'priceIn' | 'priceOut' | 'priceCacheRead' | 'priceCacheWrite' | 'dims' | 'managedEnabled' | 'minPlan' | 'active'>>

export function useUpdateCatalogModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ provider, model, patch }: { provider: string; model: string; patch: CatalogPatch }) =>
      (await platformApi.put(`/platform/ai/catalog/${encodeURIComponent(provider)}/${encodeURIComponent(model)}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.catalog }),
  })
}

export function useCreateCatalogModel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Omit<CatalogModel, 'creditsPerTurn' | 'active'> & { active?: boolean }) =>
      (await platformApi.post('/platform/ai/catalog', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.catalog }),
  })
}

// ── Cài đặt credit ────────────────────────────────────────────────
export function usePlatformAiSettings() {
  return useQuery<PlatformAiSettings | null>({
    queryKey: K.settings,
    queryFn: () => getOptional<PlatformAiSettings>('/platform/ai/settings'),
    retry: false,
  })
}

export function useSavePlatformAiSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PlatformAiSettings) => (await platformApi.put<PlatformAiSettings>('/platform/ai/settings', body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.settings })
      // Giá trị credit / biên / tỷ giá đổi → credit/lượt trong danh mục đổi theo.
      void qc.invalidateQueries({ queryKey: K.catalog })
    },
  })
}

// ── AI theo tổ chức ───────────────────────────────────────────────
export function usePlatformAiOrgs(q: string, status: string) {
  return useQuery<PlatformAiOrgsResponse | null>({
    queryKey: K.orgs(q, status),
    queryFn: async () => {
      const d = await getOptional<unknown>('/platform/ai/orgs', { ...(q ? { q } : {}), ...(status ? { status } : {}) })
      if (d === null) return null
      const totals = (d as { totals?: PlatformAiOrgsResponse['totals'] })?.totals ?? null
      return { items: asList<PlatformAiOrg>(d, 'orgs'), totals }
    },
    retry: false,
  })
}

export function useAdjustOrgCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, credits, reason }: { orgId: string; credits: number; reason: string }) =>
      (await platformApi.post(`/platform/ai/orgs/${orgId}/adjust`, { credits, reason })).data,
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: K.orgsAll })
      void qc.invalidateQueries({ queryKey: K.ledger(v.orgId) })
    },
  })
}

export function useBlockOrgAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, blocked }: { orgId: string; blocked: boolean }) =>
      (await platformApi.put(`/platform/ai/orgs/${orgId}/block`, { blocked })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.orgsAll }),
  })
}

export function usePlatformOrgLedger(orgId: string | null) {
  return useQuery<{ items: LedgerItem[]; nextCursor: string | null } | null>({
    queryKey: K.ledger(orgId),
    enabled: !!orgId,
    queryFn: () => getOptional(`/platform/ai/orgs/${orgId}/ledger`, { limit: 50 }),
    retry: false,
  })
}
